/**
 * Month switcher (Phase 3, §6.2). Prev/next chevrons around the de-DE month
 * label; a "Aktueller Monat" reset appears when the viewed month is not the
 * current one.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatMonth } from '../../lib/format.ts';
import { addMonths, currentMonth } from '../../lib/hooks/useReports.ts';
import type { MonthStr } from '../../lib/types.ts';

export interface MonthSwitcherProps {
  month: MonthStr;
  onChange: (month: MonthStr) => void;
}

export function MonthSwitcher({ month, onChange }: MonthSwitcherProps) {
  const now = currentMonth();
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        aria-label="Vorheriger Monat"
        onClick={() => onChange(addMonths(month, -1))}
        className="rounded-md p-1.5 text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300 dark:focus-visible:outline-sky-500"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden />
      </button>
      <span className="min-w-36 text-center text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {formatMonth(month)}
      </span>
      <button
        type="button"
        aria-label="Nächster Monat"
        onClick={() => onChange(addMonths(month, 1))}
        className="rounded-md p-1.5 text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300 dark:focus-visible:outline-sky-500"
      >
        <ChevronRight className="h-5 w-5" aria-hidden />
      </button>
      {month !== now ? (
        <button
          type="button"
          onClick={() => onChange(now)}
          className="ml-1 rounded-md px-2 py-1 text-xs font-medium text-sky-600 transition-colors duration-150 hover:bg-sky-50 hover:text-sky-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:text-sky-400 dark:hover:bg-sky-950 dark:hover:text-sky-300 dark:focus-visible:outline-sky-500"
        >
          Aktueller Monat
        </button>
      ) : null}
    </div>
  );
}
